import { cloneDeep } from 'lodash-es';

import type { PricingSchemeWrite } from 'src/common/external/bambi-api/bambiApi';

import { defaultValues } from './configurationDefaultValues';

import type { SchemeMetaFormValues } from './metaDefaultValues';
import type { SchemeConfigFormValues } from './PricingWizardSchemeConfigurationStep';

export const configurationFormValuesToPricingScheme = (
  values: SchemeConfigFormValues,
  workInProgressScheme: SchemeMetaFormValues,
  id?: string
) => {
  // Clone the values to avoid mutating the original values
  // specifically deleting the percentage fields below
  const valuesClone = cloneDeep(values);

  // Because the backend uses cents for both flat and percentage fees,
  // we need to remove it from values and handle it separately
  // This will be able to be removed once the backend is updated
  const lateCancellationPercentage =
    valuesClone.late_cancellation_fee_percentage;
  delete valuesClone.late_cancellation_fee_percentage;
  // Likewise, with no show fee
  const noShowPercentage = valuesClone.no_show_fee_percentage;
  delete valuesClone.no_show_fee_percentage;

  // this was handled differently in the initial implementation of the pricing wizard
  // when creating a new pricing scheme, we don't pass it to the BE when falsy
  // when updating a pricing scheme, we pass the default value so user can clear the value
  const longDistanceMileageRateCents =
    valuesClone.long_distance_mileage_rate_cents ||
    (id ? defaultValues.long_distance_mileage_rate_cents : undefined);
  const longDistanceMileageStart =
    valuesClone.long_distance_mileage_start ||
    (id ? defaultValues.long_distance_mileage_start : undefined);

  const pricingScheme: PricingSchemeWrite = {
    ...workInProgressScheme,
    base_fee_cents: valuesClone.base_fee_cents || defaultValues.base_fee_cents,
    minimum_price_cents:
      valuesClone.minimum_price_cents || defaultValues.minimum_price_cents,
    mileage_rounding:
      valuesClone.mileage_rounding || defaultValues.mileage_rounding,
    price_rounding: valuesClone.price_rounding || defaultValues.price_rounding,
    price_rounding_interval:
      valuesClone.price_rounding_interval ||
      defaultValues.price_rounding_interval,
    loaded_mileage_rate_cents:
      valuesClone.loaded_mileage_rate_cents ||
      defaultValues.loaded_mileage_rate_cents,
    free_loaded_miles:
      valuesClone.free_loaded_miles || defaultValues.free_loaded_miles,
    long_distance_mileage_rate_cents: longDistanceMileageRateCents,
    long_distance_mileage_start: longDistanceMileageStart,
    unloaded_mileage_rate_cents:
      valuesClone.unloaded_mileage_rate_cents ||
      defaultValues.unloaded_mileage_rate_cents,
    free_unloaded_miles:
      valuesClone.free_unloaded_miles || defaultValues.free_unloaded_miles,
    unloaded_return_mileage_rate_cents:
      valuesClone.unloaded_return_mileage_rate_cents ||
      defaultValues.unloaded_return_mileage_rate_cents,
    free_unloaded_return_miles:
      valuesClone.free_unloaded_return_miles ||
      defaultValues.free_unloaded_return_miles,
    wait_time_rate_cents:
      valuesClone.wait_time_rate_cents || defaultValues.wait_time_rate_cents,
    wait_time_period_minutes:
      valuesClone.wait_time_period_minutes ||
      defaultValues.wait_time_period_minutes,
    wait_time_free_minutes:
      valuesClone.wait_time_free_minutes ||
      defaultValues.wait_time_free_minutes,
    late_cancellation_fee_cents:
      valuesClone.late_cancellation_fee_type === 'Percentage'
        ? lateCancellationPercentage ||
          defaultValues.late_cancellation_fee_percentage
        : valuesClone.late_cancellation_fee_cents ||
          defaultValues.late_cancellation_fee_cents,
    late_cancellation_period_minutes:
      valuesClone.late_cancellation_period_minutes ||
      defaultValues.late_cancellation_period_minutes,
    late_cancellation_fee_type:
      valuesClone.late_cancellation_fee_type ||
      defaultValues.late_cancellation_fee_type,
    no_show_fee_cents:
      valuesClone.no_show_fee_type === 'Percentage'
        ? noShowPercentage || defaultValues.no_show_fee_cents
        : valuesClone.no_show_fee_cents || defaultValues.no_show_fee_cents,
    no_show_fee_type:
      valuesClone.no_show_fee_type || defaultValues.no_show_fee_type,
    after_hours_fee_cents: !(
      valuesClone.business_hours_start_time &&
      valuesClone.business_hours_end_time
    )
      ? 0
      : valuesClone.after_hours_fee_cents ||
        defaultValues.after_hours_fee_cents,
    business_hours_start_time: valuesClone.business_hours_start_time || null,
    business_hours_end_time: valuesClone.business_hours_end_time || null,
    holiday_fee_cents:
      valuesClone.holiday_fee_cents || defaultValues.holiday_fee_cents,
    observed_holidays:
      valuesClone.observed_holidays ?? defaultValues.observed_holidays,
    weekend_fee_cents:
      valuesClone.weekend_fee_cents || defaultValues.weekend_fee_cents,
    stair_fee_type: valuesClone.stair_fee_type || defaultValues.stair_fee_type,
    stair_fee_cents:
      valuesClone.stair_fee_cents || defaultValues.stair_fee_cents,
    minimum_steps: valuesClone.minimum_steps || defaultValues.minimum_steps,
    wheelchair_rental_fee_cents:
      valuesClone.wheelchair_rental_fee_cents ||
      defaultValues.wheelchair_rental_fee_cents,
    oxygen_fee_cents:
      valuesClone.oxygen_fee_cents || defaultValues.oxygen_fee_cents,
    additional_attendant_rate_cents:
      valuesClone.additional_attendant_rate_cents ||
      defaultValues.additional_attendant_rate_cents,
    additional_passenger_rate_cents:
      valuesClone.additional_passenger_rate_cents ||
      defaultValues.additional_passenger_rate_cents,
    bariatric_passenger_fee_cents:
      valuesClone.bariatric_passenger_fee_cents ||
      defaultValues.bariatric_passenger_fee_cents,
    infectious_disease_fee_cents:
      valuesClone.infectious_disease_fee_cents ||
      defaultValues.infectious_disease_fee_cents,
    same_day_booking_fee_cents:
      valuesClone.same_day_booking_fee_cents ||
      defaultValues.same_day_booking_fee_cents,
    car_seat_fee_cents:
      valuesClone.car_seat_fee_cents || defaultValues.car_seat_fee_cents,
    booster_seat_fee_cents:
      valuesClone.booster_seat_fee_cents ||
      defaultValues.booster_seat_fee_cents,
    ride_alone_fee_cents:
      valuesClone.ride_alone_fee_cents || defaultValues.ride_alone_fee_cents,
  };
  return pricingScheme;
};
